/* eslint-disable react/no-danger */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import get from 'lodash/get';
import { getThemeFromConfig } from '../../cruxComponents/utils';
import CustomSubHeader from './CustomSubHeader';
import { isCustomRole, sanitizeInnerHtml } from '../../utils';

const CustomHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  ${({ showSubHeader }) =>
    showSubHeader ? 'flex-direction: column;' : 'background: #18355e;height: 128px;'}
  ${({ headerPosition }) =>
    headerPosition === 'fixed' &&
    'position: fixed; top: 0; left: 0; width: 100%; z-index: 2147483647;'}
  @media (forced-colors: active) {
    border-bottom: 1px solid Highlight;
  }
`;

const HeaderContainer = styled.div`
  ${({ showSubHeader }) =>
    showSubHeader
      ? 'background: #18355e;height: 100px;border-bottom: 1px solid transparent;'
      : 'height: 100%;'}
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${({ pageId, screenViewport }) =>
    pageId === 'quote' && screenViewport === 'desktop' ? '0 65px 0 0' : '0 auto'};
  width: 100%;
  @media (max-width: 425px) {
    margin: ${({ bannerInfo }) => bannerInfo && '0 5px'};
  }
`;

const CustomLogoWrapper = styled.div`
  width: 33%;
  margin-top: 0;
  position: relative;
  max-width: 280px;
  margin: 0 17px;
  @media (max-width: 425px) {
    margin: 0;
    width: 50%;
  }
  @media (min-width: 768px) and (max-width: 1024px) {
    max-width: 162px;
  }
  & a {
    width: 100%;
    margin: 0 20px;
    @media (min-width: 1025px) and (max-width: 1040px) {
      margin: 0 5px;
    }
    @media (min-width: 1040px) and (max-width: 1060px) {
      margin: 0 15px;
    }
    @media (max-width: 768px) {
      margin: 0;
    }
  }
  & img {
    width: 190px;
    height: 100px;
    @media (max-width: 768px) {
      max-width: 162px;
    }
  }
`;

const Banner = styled.div`
  width: 67%;
  margin: 0 40px;
  max-width: 980px;
  padding: 10px 0;
  font-family: Avenir-Heavy;
  line-height: 24px;
  font-size: 26px;
  color: ${({ bannerStyles }) => bannerStyles.color};
  font-weight: ${({ bannerStyles }) => bannerStyles.fontWeight};
  ${({ alignBanner }) =>
    alignBanner === 'right' ? 'width: 100%; text-align: right' : 'text-align: center'};
  & a.tel {
    text-align: right;
    font-family: Avenir-Book;
    font-size: 18px;
    color: #ffffff;
    border: 3px solid transparent;
    @media (forced-colors: active) {
      border: none;
    }
    margin-right: ${({ pageId, screenViewport }) =>
      pageId === 'quote' && screenViewport === 'desktop' ? '-35px' : '0px'};
    &:focus {
      outline: 3px solid #fff;
      border: 3px solid transparent;
    }
    @media (max-width: 280px) {
      font-size: 14px;
    }
  }
  @media (min-width: 768px) and (max-width: 1024px) {
    margin: 0 20px 0 0;
    width: 75%;
  }
  @media (max-width: 980px) {
    ${({ alignBanner, role }) =>
      alignBanner === 'right' && !isCustomRole(role) ? '' : 'text-align: left; margin-left: 20px;'};
    font-size: 20px;
  }
  @media (max-width: 425px) {
    font-size: 16px;
    margin: 0 20px 0 0px;
    width: 50%;
  }
`;

const SkipNavLink = styled.a`
  background: ${({ buttonBgColor }) => buttonBgColor};
  color: white;
  margin-top: ${({ skipMarginTop }) => skipMarginTop};
  padding: 0.5rem 1.5rem;
  border: 2px solid white;
  border-radius: 0 0 0.25rem 0.25rem;
  left: 0.5rem;
  position: absolute;
  transform: translateY(-240%);
  transition: transform 325ms ease-in;
  :focus,
  :hover {
    cursor: pointer;
    transform: translateY(-120%);
    text-decoration: underline;
    outline: 2px solid #262d3f;
  }
  &.skip-nav-link:hover {
    color: white;
  }
`;

const CustomHeader = ({
  bannerStyles,
  bannerInfo,
  branding,
  role,
  pageSubHeader,
  routeChange,
  theme,
  isSmallScreen,
  creditCardStatusMessage,
  activeReviewedGroupId,
  currentBreadcrumb,
}) => {
  const { pageId, screenViewport } = theme;
  const { headerPosition, alignBanner } = getThemeFromConfig(theme, 'globalHeaderTheme');
  const { button: skipNavigationButton } = getThemeFromConfig(theme, 'skipNavigation');
  const position = get(headerPosition, theme.screenViewport);

  const showSubHeader = pageSubHeader && pageSubHeader.show;

  useEffect(() => {
    if (routeChange && bannerInfo) {
      const _headerTelEl = document.querySelector('[name="custom-header-banner"] a.tel');
      if (
        _headerTelEl &&
        _headerTelEl.focus &&
        !creditCardStatusMessage &&
        !activeReviewedGroupId
      ) {
        _headerTelEl.focus();
      } else if (!_headerTelEl) {
        // focus on first active breadcrumb on routeChange if tel number is not present in header
        const breadcrumbEls = [...document.querySelectorAll('[name="card-wrapper"]')];
        const activeBcrumbEls = breadcrumbEls?.find((el) => el?.firstChild?.tabIndex === 0);
        const activeBcrumbEl = activeBcrumbEls?.firstChild;
        if (activeBcrumbEl && activeBcrumbEl.focus) {
          activeBcrumbEl.focus();
        }
      }
    }
  }, [routeChange, bannerInfo]);

  // Override role specific alignment for knockout and nonclean page
  role = ['knockout', 'nonclean'].includes(pageId) ? '' : role;

  const skipNavigationHandler = () => {
    const _firstHeader = document.querySelector('h1:first-child');
    const _secondHeader = document.querySelector('h2:first-child');
    if (_firstHeader && _firstHeader.focus) {
      _firstHeader.style.cssText += '&:focus{outline: 2px solid #262d3f}';
      _firstHeader.focus();
    } else if (_secondHeader && _secondHeader.focus) {
      _secondHeader.setAttribute('tabindex', '-1');
      _secondHeader.style.cssText += '&:focus{outline: 2px solid #262d3f}';
      _secondHeader.focus();
    }
    window.scroll({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleSkipLinkOnKeydown = (event) => {
    const eventKey = event?.key;
    if (eventKey === 'Enter') {
      const element = document.querySelector('.skip-nav-link');
      element.click();
    }
  };

  const { pathname } = window.location;
  const skipMarginTop = isCustomRole(role) && pathname.includes('/questions') ? '3rem' : '1rem';

  return (
    <CustomHeaderWrapper
      name='custom-header-wrapper'
      role={ role }
      headerPosition={ position }
      showSubHeader={ showSubHeader }
    >
      {!isSmallScreen && (
        <SkipNavLink
          skipMarginTop={ skipMarginTop }
          buttonBgColor={ skipNavigationButton.selectedBgColor }
          className='skip-nav-link'
          tabIndex='0'
          onClick={ () => skipNavigationHandler() }
          onKeyDown={ handleSkipLinkOnKeydown }
        >
          Skip To Main Content
        </SkipNavLink>
      )}
      <HeaderContainer
        screenViewport={ screenViewport }
        pageId={ pageId }
        name='custom-header-container'
        bannerInfo={ bannerInfo }
        showSubHeader={ showSubHeader }
      >
        <CustomLogoWrapper pageId={ pageId } name='custom-logo'>
          <img src={ branding.lightLogo } alt={ `${branding.altText} logo` } />
        </CustomLogoWrapper>
        <Banner
          screenViewport={ screenViewport }
          pageId={ pageId }
          name='custom-header-banner'
          bannerStyles={ bannerStyles }
          alignBanner={ alignBanner }
          role={ role }
        >
          {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
          {bannerInfo && (
            <span dangerouslySetInnerHTML={ { __html: sanitizeInnerHtml(bannerInfo) } } />
          )}
        </Banner>
      </HeaderContainer>
      {showSubHeader && (
        <CustomSubHeader
          pageSubHeader={ pageSubHeader }
          showSubHeader={ showSubHeader }
          pageId={ pageId }
          currentBreadcrumb={ currentBreadcrumb }
        />
      )}
    </CustomHeaderWrapper>
  );
};

function mapStateToProps({ questions, router, meta }) {
  const pathname = get(router, 'location.pathname', '');
  const activeReviewedGroupId = get(questions, 'activeReviewedGroupId', null);
  const currentBreadcrumb = questions.breadcrumbs?.currentBreadcrumb;
  return {
    bannerInfo: questions.bannerInfo,
    role: questions.role,
    pathname,
    pageSubHeader: questions.pageSubHeader,
    routeChange: questions.routeChange,
    questions,
    isSmallScreen: meta.isSmallScreen,
    creditCardStatusMessage: questions?.creditCardStatusMessage,
    activeReviewedGroupId,
    currentBreadcrumb,
  };
}
export default withTheme(connect(mapStateToProps)(CustomHeader));
